import { useEffect, useMemo, useState } from "react"
import {
  Banknote,
  Check,
  ConciergeBell,
  CreditCard,
  Lock,
  Minus,
  Plus,
  ReceiptText,
  Smartphone,
  Split,
  User,
} from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"
import { formatMoney, parseDecimal } from "./money"
import { t } from "@/lib/i18n"
import type { BillOrderLine, BillParticipant, BillSplit, PaymentMethodId } from "./types"
import { dinerServiceFee } from "./pickup-cart"

type PayMethod = {
  id: PaymentMethodId
  title: string
  subtitle: string
  Icon: typeof CreditCard
}

const METHOD_ORDER: PaymentMethodId[] = ["digital_in_app", "physical_card_terminal", "cash"]

const METHOD_COPY: Record<PaymentMethodId, Omit<PayMethod, "id">> = {
  digital_in_app: {
    title: t("ui.payments.methods.long.digital_in_app"),
    subtitle: t("ui.react.features.user.pay_sheet.digital_checkout_subtitle"),
    Icon: Smartphone,
  },
  physical_card_terminal: {
    title: t("ui.payments.methods.long.physical_card_terminal"),
    subtitle: t("ui.react.features.user.pay_sheet.terminal_subtitle"),
    Icon: CreditCard,
  },
  cash: {
    title: t("ui.payments.methods.long.cash"),
    subtitle: t("ui.react.features.user.pay_sheet.cash_subtitle"),
    Icon: Banknote,
  },
}

// The typed-amount option rides in the same group as the percentages, so it
// needs a value that cannot collide with one of them.
const CUSTOM_TIP_VALUE = "custom" as const

// The kit's toggle marks "on" with `bg-accent`, which in light mode is
// oklch(0.97) sitting on a oklch(0.985) sheet — a 1.5% lightness delta, and the
// same value `variant="outline"` uses for hover. Measured on screen, the chosen
// tip was not identifiable. This borrows the active treatment the rest of this
// drawer already uses (ModeCard, the method label, OrderRow) so the selection is
// carried by a border rather than by a wash nobody can see. The kit-wide fix
// belongs in components/ui/toggle, which every domain shares.
const TIP_OPTION_CLASS =
  "h-auto flex-1 flex-col gap-0 px-1 py-2 data-[state=on]:border-primary data-[state=on]:bg-primary/5"
// The diner's starting position is declining. Anything else means a diner who
// taps straight through pays a tip they never chose, which is what this screen
// did before — it opened on 10% and offered no way back to zero except the
// typed-amount escape hatch. Location::TipPolicy carries the full reasoning,
// including why an unchosen tip is a tax question and not only a taste one.
const NO_TIP_PERCENTAGE = 0
const MIN_PAYERS = 2
const MAX_PAYERS = 20

// Stripe will not charge a card below roughly ten pesos. The warning here is
// advisory only: the payer's method is not known while N is being chosen, and
// the binding rejection happens server-side at confirmation.
const DIGITAL_MINIMUM = 10

// Tied to the sentinel rather than repeating the literal: the component compares
// against both, and two copies of one string are two chances to drift.
type TipMode = "percentage" | typeof CUSTOM_TIP_VALUE

/**
 * How the table settles. `choose` is the question every table asks out loud;
 * the other two are the answers. A bill that already carries a split skips
 * straight to `divide`, because whoever opened it answered for everyone.
 */
type SettleMode = "choose" | "items" | "divide"

export type PayConfirmation = {
  method: PaymentMethodId
  orderIds: number[]
  tipPercentage: number
  tipAmount?: number
  /** Present when this is a share of a split rather than an item selection. */
  splitId?: number
}

type PaySheetProps = {
  open: boolean
  onClose: () => void
  onConfirm: (payment: PayConfirmation) => void
  /** Opens the split. The server freezes the set and answers with the share. */
  onOpenSplit?: (payerCount: number) => void
  onCancelSplit?: () => void
  /** Every order on the bill, paid ones included so the diner sees them. */
  tableOrders: BillOrderLine[]
  /** The subset this diner ordered; drives the "only mine" shortcut. */
  myOrders: BillOrderLine[]
  /** The roster: seeds the default payer count, and names the owner of each line. */
  participants: BillParticipant[]
  /** The running split, when the table already chose to divide. */
  split?: BillSplit | null
  /** Pickup has one payer and rejects tips, so none of this applies. */
  isPickup?: boolean
  currency: string
  /**
   * The percentages this restaurant suggests, zero first. Server-driven: the
   * location owns the list, and `Location#tip_percentage_options` guarantees the
   * leading zero, so this component never has to defend that rule itself.
   */
  tipPercentages: number[]
  /**
   * The rate a pickup order settled digitally carries, as the server sends it.
   * Absent or "0.0" means no fee, which is every dine-in bill and most pickup.
   */
  pickupDinerFeePercentage?: string
  enabledPaymentMethods: PaymentMethodId[]
  isSubmitting?: boolean
  errors?: string[]
  notice?: string | null
}

export function PaySheet({
  open,
  onClose,
  onConfirm,
  onOpenSplit,
  onCancelSplit,
  tableOrders,
  myOrders,
  participants,
  split = null,
  isPickup = false,
  currency,
  tipPercentages,
  pickupDinerFeePercentage,
  enabledPaymentMethods,
  isSubmitting = false,
  errors = [],
  notice = null,
}: PaySheetProps) {
  const methods = useMemo(
    () => METHOD_ORDER.filter((id) => enabledPaymentMethods.includes(id)).map((id) => ({ id, ...METHOD_COPY[id] })),
    [enabledPaymentMethods]
  )
  const [method, setMethod] = useState<PaymentMethodId | null>(methods[0]?.id ?? null)
  const [mode, setMode] = useState<SettleMode>("choose")
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [payerCount, setPayerCount] = useState<number>(Math.max(MIN_PAYERS, participants.length || MIN_PAYERS))
  const [tipMode, setTipMode] = useState<TipMode>("percentage")
  const [tipPercentage, setTipPercentage] = useState<number>(NO_TIP_PERCENTAGE)
  // `tipMode` alone drives both which value wins and whether the field is on
  // screen. There is no separate disclosure flag: the option is selected in the
  // same group as the percentages, so being selected *is* being open.
  const [customTip, setCustomTip] = useState<string>("")

  // Order lines name their owner but do not carry initials; the roster does.
  // Staff-placed lines have a null `userId` on purpose and so match nothing here.
  const participantsById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant])),
    [participants]
  )

  const selectedMethod = method && enabledPaymentMethods.includes(method) ? method : (methods[0]?.id ?? null)

  useEffect(() => {
    if (!open) return
    if (!selectedMethod) setMethod(methods[0]?.id ?? null)
    else if (selectedMethod !== method) setMethod(selectedMethod)
  }, [method, methods, open, selectedMethod])

  // A running split has already answered the question, for everyone.
  useEffect(() => {
    if (split) setMode("divide")
  }, [split])

  // Pickup never asks: one order, one payer.
  const effectiveMode: SettleMode = isPickup ? "items" : mode

  const payable = tableOrders.filter((order) => order.paymentStatus === "unpaid")
  const payableMine = myOrders.filter((order) => order.paymentStatus === "unpaid")

  const selectedOrders = isPickup
    ? payable
    : payable.filter((order) => selectedIds.includes(order.id))

  const subtotalOf = (orders: BillOrderLine[]) =>
    orders.reduce((sum, order) => sum + parseDecimal(order.lineTotal), 0)

  const shareAmount = split?.shareAmount ? parseDecimal(split.shareAmount) : 0
  const subtotal = effectiveMode === "divide" && split ? shareAmount : subtotalOf(selectedOrders)

  const customTipAmount = Math.max(0, parseDecimal(customTip))
  const tipAmount = isPickup ? 0 : tipMode === "custom" ? customTipAmount : subtotal * (tipPercentage / 100)
  // Same rule the backend applies in `Location#fee_percentages_for`: a pickup
  // sale settled through Stripe, and nothing else. Cash carries no fee because
  // there is no `application_fee_amount` to collect one with, so quoting one
  // here would promise a charge that never happens.
  //
  // `dinerServiceFee` is the cart screen's, reused rather than reimplemented:
  // two copies of this rounding is how the sheet and the charge end up a cent
  // apart.
  const serviceFee =
    isPickup && selectedMethod === "digital_in_app"
      ? dinerServiceFee(subtotal, pickupDinerFeePercentage)
      : 0
  const total = subtotal + tipAmount + serviceFee

  const toggleOrder = (id: number) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    )

  // Both shortcuts toggle: tapping the one that is already satisfied clears the
  // selection rather than re-applying it, so the same control undoes itself
  // instead of stranding the diner with no way back to an empty list.
  const applyShortcut = (orders: BillOrderLine[]) =>
    setSelectedIds((current) => (sameSelection(current, orders) ? [] : orders.map((order) => order.id)))

  const selectAll = () => applyShortcut(payable)
  const selectMine = () => applyShortcut(payableMine)

  const allSelected = sameSelection(selectedIds, payable)
  const mineSelected = sameSelection(selectedIds, payableMine)

  const selectPercentage = (value: number) => {
    setTipMode("percentage")
    setTipPercentage(value)
  }

  // Entering the option seeds it with whatever the tip is worth right now, so
  // choosing "otro monto" edits the amount on screen instead of silently
  // dropping it to zero. A previously typed amount is kept, not overwritten.
  //
  // Nothing selected yet means nothing to seed: writing "0.00" would look like a
  // decided zero tip and, being non-empty, would never be re-seeded once items
  // were picked. An empty field asks to be typed into, which is the truth.
  const chooseCustomTip = () => {
    if (customTip === "" && tipAmount > 0) setCustomTip(tipAmount.toFixed(2))
    setTipMode("custom")
  }

  // No `setTipMode` here: the field only renders under `custom`, so it is the
  // only mode this can be called from.
  const editCustomTip = (raw: string) => {
    setCustomTip(raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))
  }

  const tipPayload = isPickup
    ? { tipPercentage: 0, tipAmount: undefined }
    : tipMode === "custom"
      ? { tipPercentage: 0, tipAmount: customTipAmount }
      : { tipPercentage, tipAmount: undefined }

  const hasMethods = methods.length > 0
  const readyToPay = split ? shareAmount > 0 : selectedOrders.length > 0
  const submitDisabled = !selectedMethod || isSubmitting || !hasMethods || !readyToPay
  const submitLabel =
    selectedMethod === "digital_in_app"
      ? t("ui.react.features.user.pay_sheet.continue_to_checkout")
      : t("ui.react.features.user.pay_sheet.request_staff_charge")

  // The displayed share is a quote. The server recomputes it from the split's
  // live balance when this lands, so a stale screen cannot pay a stale amount.
  const confirm = () => {
    if (!selectedMethod || !readyToPay) return
    onConfirm({
      method: selectedMethod,
      orderIds: split ? [] : selectedOrders.map((order) => order.id),
      splitId: split?.id,
      tipPercentage: tipPayload.tipPercentage,
      tipAmount: tipPayload.tipAmount,
    })
  }

  const projectedShare = payable.length > 0 ? subtotalOf(payable) / payerCount : 0
  const belowDigitalMinimum =
    enabledPaymentMethods.includes("digital_in_app") && projectedShare > 0 && projectedShare < DIGITAL_MINIMUM

  const showPaymentControls = isPickup || effectiveMode === "items" || Boolean(split)

  return (
    <ResponsiveDrawer open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
        <div className="mx-auto flex w-full max-w-lg flex-col overflow-y-auto" data-testid="pay-sheet">
          <DrawerHeader>
            <DrawerTitle>{t("ui.react.features.user.pay_sheet.pay")}</DrawerTitle>
            <DrawerDescription>
              {effectiveMode === "choose"
                ? t("ui.react.features.user.pay_sheet.settle_hint")
                : t("ui.react.features.user.pay_sheet.choose_method_payment")}
            </DrawerDescription>
          </DrawerHeader>

          <div className="space-y-4 px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            {notice ? (
              <p
                className="rounded-lg border border-status-available/40 bg-status-available/10 px-3 py-2 text-xs font-medium text-status-available"
                role="status"
              >
                {notice}
              </p>
            ) : null}

            {errors.length > 0 ? (
              <div
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
                role="alert"
              >
                {errors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            ) : null}

            {effectiveMode === "choose" && (
              <section>
                <SectionLabel>{t("ui.react.features.user.pay_sheet.settle_question")}</SectionLabel>
                <div className="grid gap-2">
                  <ModeCard
                    testId="settle-mode-items"
                    icon={<User className="size-4" />}
                    title={t("ui.react.features.user.pay_sheet.mode_items")}
                    hint={t("ui.react.features.user.pay_sheet.mode_items_hint")}
                    onSelect={() => setMode("items")}
                  />
                  <ModeCard
                    testId="settle-mode-divide"
                    icon={<Split className="size-4" />}
                    title={t("ui.react.features.user.pay_sheet.mode_divide")}
                    hint={t("ui.react.features.user.pay_sheet.mode_divide_hint")}
                    onSelect={() => setMode("divide")}
                  />
                </div>
              </section>
            )}

            {effectiveMode === "items" && !isPickup && (
              <section>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <SectionLabel className="mb-0">
                    {t("ui.react.features.user.pay_sheet.choose_items")}
                  </SectionLabel>
                  {/* Two words each cost more width than the item names they sit
                      above. As icons they are a pair of switches, which is what
                      they always were. The glyphs name the two *scopes* — the
                      bill, and you — rather than Users/User, whose silhouettes
                      are near-identical at 16px and whose meaning collides with
                      the cart screen, where Users marks a table-mate's line. */}
                  <div className="flex gap-1.5">
                    <ShortcutToggle
                      testId="select-all-items"
                      icon={<ReceiptText />}
                      label={t("ui.react.features.user.pay_sheet.select_all")}
                      pressed={allSelected}
                      onClick={selectAll}
                    />
                    <ShortcutToggle
                      testId="select-my-items"
                      icon={<User />}
                      label={t("ui.react.features.user.pay_sheet.select_mine")}
                      pressed={mineSelected}
                      onClick={selectMine}
                      disabled={payableMine.length === 0}
                    />
                  </div>
                </div>

                <ul className="space-y-1.5" data-testid="pay-item-list">
                  {tableOrders.map((order) => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      currency={currency}
                      owner={order.userId === null ? null : participantsById.get(order.userId) ?? null}
                      checked={selectedIds.includes(order.id)}
                      onToggle={() => toggleOrder(order.id)}
                    />
                  ))}
                </ul>

                {selectedOrders.length === 0 && (
                  <p className="mt-2 px-0.5 text-xs text-muted-foreground">
                    {t("ui.react.features.user.pay_sheet.nothing_selected")}
                  </p>
                )}
              </section>
            )}

            {effectiveMode === "divide" && !split && (
              <section className="space-y-3">
                <SectionLabel>{t("ui.react.features.user.pay_sheet.people_paying")}</SectionLabel>
                <div className="flex items-center justify-between rounded-xl border p-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setPayerCount((value) => Math.max(MIN_PAYERS, value - 1))}
                    disabled={payerCount <= MIN_PAYERS}
                    aria-label={t("ui.react.features.user.pay_sheet.people_paying")}
                    data-testid="payer-count-decrement"
                  >
                    <Minus />
                  </Button>
                  <span className="text-lg font-semibold tabular-nums" data-testid="payer-count-value">
                    {payerCount}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setPayerCount((value) => Math.min(MAX_PAYERS, value + 1))}
                    disabled={payerCount >= MAX_PAYERS}
                    aria-label={t("ui.react.features.user.pay_sheet.people_paying")}
                    data-testid="payer-count-increment"
                  >
                    <Plus />
                  </Button>
                </div>

                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-muted-foreground">{t("ui.react.features.user.pay_sheet.your_share")}</span>
                  <span className="font-semibold tabular-nums" data-testid="projected-share">
                    {formatMoney(projectedShare, { currency })}
                  </span>
                </div>

                {belowDigitalMinimum && (
                  <p
                    className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
                    role="status"
                  >
                    {t("ui.react.features.user.pay_sheet.share_below_minimum")}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setMode("choose")}>
                    {t("ui.react.features.user.pay_sheet.back")}
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={isSubmitting || payable.length === 0}
                    onClick={() => onOpenSplit?.(payerCount)}
                    data-testid="open-split-button"
                  >
                    {isSubmitting
                      ? t("ui.react.features.user.pay_sheet.opening_split")
                      : t("ui.react.features.user.pay_sheet.confirm_divide", { count: payerCount })}
                  </Button>
                </div>
              </section>
            )}

            {/* The split summary carries no border. With one it reads as a selectable
                row and collides with the chosen payment method just below, which is a
                --primary wash inside a --primary border. Unframed it reads as the
                section's own heading and the ambiguity goes away without changing a
                single colour. */}
            {split && (
              <section className="space-y-2 rounded-xl bg-brand/5 p-3" data-testid="split-summary">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium">
                    {t("ui.react.features.user.pay_sheet.mode_divide")} ·{" "}
                    <span className="tabular-nums">{split.payerCount}</span>
                  </span>
                  <span className="text-lg font-semibold tabular-nums" data-testid="split-share-amount">
                    {formatMoney(shareAmount, { currency })}
                  </span>
                </div>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {t("ui.react.features.user.pay_sheet.payers_left", { count: split.payersRemaining })}
                </p>
                {onCancelSplit && !split.settling && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={onCancelSplit}
                    data-testid="cancel-split-button"
                  >
                    {t("ui.react.features.user.pay_sheet.cancel_split")}
                  </Button>
                )}
              </section>
            )}

            {showPaymentControls && (
              <>
                {!isPickup && (
                  <section>
                    <SectionLabel>{t("ui.react.features.user.pay_sheet.tip")}</SectionLabel>
                    <ToggleGroup
                      type="single"
                      value={tipMode === "custom" ? CUSTOM_TIP_VALUE : String(tipPercentage)}
                      onValueChange={(value) => {
                        if (!value) return
                        if (value === CUSTOM_TIP_VALUE) chooseCustomTip()
                        else selectPercentage(Number(value))
                      }}
                      variant="outline"
                      spacing={1}
                      className="w-full"
                      aria-label={t("ui.react.features.user.pay_sheet.tip")}
                      data-testid="tip-options"
                    >
                      {tipPercentages.map((percentage) => {
                        const active = tipMode === "percentage" && tipPercentage === percentage
                        return (
                          <ToggleGroupItem
                            key={percentage}
                            value={String(percentage)}
                            data-testid={`tip-button-${percentage}`}
                            data-active={active ? "true" : "false"}
                            className={TIP_OPTION_CLASS}
                            // No aria-label: it used to be just the percentage,
                            // which overrode the content and hid the preview
                            // underneath — the one thing the sub-line is for.
                          >
                            <span className="text-sm font-semibold tabular-nums">{percentage}%</span>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {formatMoney(subtotal * (percentage / 100), { currency })}
                            </span>
                          </ToggleGroupItem>
                        )
                      })}

                      {/* Last, not first. "Otro" means *other than* — leading the
                          row it referred to four options the diner had not read
                          yet. It is also the escape hatch rather than the fast
                          path, and every tip UI a diner here already uses puts it
                          at the end. Keeping it last leaves the four presets on
                          the positions a returning diner learned. */}
                      <ToggleGroupItem
                        value={CUSTOM_TIP_VALUE}
                        data-testid="tip-button-custom"
                        data-active={tipMode === "custom" ? "true" : "false"}
                        className={TIP_OPTION_CLASS}
                        aria-label={t("ui.react.features.user.pay_sheet.custom_tip_label")}
                      >
                        <span className="text-sm font-semibold">
                          {t("ui.react.features.user.pay_sheet.custom_tip_short")}
                        </span>
                        {/* The siblings put what the option costs here. This one
                            has no preview to give — the amount is the diner's to
                            write, and it is already on screen in the field below.
                            An em dash keeps the slot a money slot; echoing the
                            field would print two figures that disagree with each
                            other on every keystroke. */}
                        <span className="text-xs text-muted-foreground" aria-hidden="true">
                          —
                        </span>
                      </ToggleGroupItem>
                    </ToggleGroup>

                    {/* The field belongs to the option above it and appears with
                        it. The option carries the visible name, so the field
                        takes it as an aria-label rather than repeating it. */}
                    {tipMode === "custom" && (
                      <div className="relative mt-2">
                        <span
                          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
                          aria-hidden="true"
                        >
                          $
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          // The drawer is not a form, so Enter had nothing to submit
                          // and the mobile key did nothing at all. `done` is the
                          // whole fix: it relabels the key ("Ir" promised a
                          // navigation that was never going to happen) and the
                          // platform dismisses the keyboard itself.
                          //
                          // Deliberately no blur() here. Vaul restores the drawer's
                          // height from a visualViewport handler gated on
                          // `isInput(document.activeElement) || keyboardIsOpen`, and
                          // Android fires several resize events while the keyboard
                          // animates away. Blurring drops focus before the first of
                          // them, so the last one — the one where the viewport is
                          // finally back to full height — fails that guard and never
                          // runs. The drawer keeps the height it was given mid
                          // animation and leaves dead space below itself.
                          enterKeyHint="done"
                          value={customTip}
                          onChange={(event) => editCustomTip(event.target.value)}
                          // Select on mount only. As an onFocus handler this also
                          // fired when the diner tapped back in to fix a typo,
                          // wiping the whole amount on their next keystroke.
                          ref={selectOnMount}
                          // Its own name: sharing `custom_tip_label` with the
                          // toggle above made a screen reader announce two
                          // different controls as "Otro monto" in a row.
                          aria-label={t("ui.react.features.user.pay_sheet.custom_tip_field_label")}
                          data-testid="tip-amount-input"
                          className="pl-5 text-right tabular-nums"
                        />
                      </div>
                    )}
                  </section>
                )}

                <section className="rounded-xl border bg-muted/30 p-3" data-testid="cart-totals">
                  <div className="flex justify-between py-0.5 text-sm">
                    <span className="text-muted-foreground">{t("ui.react.features.user.pay_sheet.subtotal")}</span>
                    <span className="tabular-nums">{formatMoney(subtotal, { currency })}</span>
                  </div>
                  {serviceFee > 0 && (
                    <div className="flex justify-between py-0.5 text-sm">
                      <span className="text-muted-foreground">
                        {t("ui.react.features.user.pickup_cart.service_fee", {
                          percentage: pickupDinerFeePercentage,
                        })}
                      </span>
                      <span className="tabular-nums" data-testid="totals-service-fee-value">
                        {formatMoney(serviceFee, { currency })}
                      </span>
                    </div>
                  )}
                  {!isPickup && (
                    <div className="flex justify-between py-0.5 text-sm">
                      <span className="text-muted-foreground">
                        {t("ui.react.features.user.pay_sheet.tip")}
                        {tipMode === "percentage" ? (
                          <span className="tabular-nums"> ({tipPercentage}%)</span>
                        ) : null}
                      </span>
                      <span className="tabular-nums" data-testid="totals-tip-value">
                        {formatMoney(tipAmount, { currency })}
                      </span>
                    </div>
                  )}
                  <div className="mt-2 flex items-baseline justify-between border-t pt-2.5 font-semibold">
                    <span className="text-sm">{t("ui.react.features.user.pay_sheet.total_payable")}</span>
                    <span data-testid="cart-total-value" className="text-lg tabular-nums">
                      {formatMoney(total, { currency })}
                    </span>
                  </div>
                </section>

                <section>
                  <SectionLabel>{t("ui.react.features.user.pay_sheet.method")}</SectionLabel>
                  {!hasMethods ? (
                    <p
                      className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
                      role="alert"
                    >
                      {t("ui.react.features.user.pay_sheet.no_payment_methods")}
                    </p>
                  ) : (
                    <RadioGroup
                      value={selectedMethod ?? ""}
                      onValueChange={(value) => setMethod(value as PaymentMethodId)}
                      className="gap-2"
                      disabled={isSubmitting}
                    >
                      {methods.map((option) => {
                        const active = selectedMethod === option.id
                        const Icon = option.Icon
                        return (
                          <label
                            key={option.id}
                            data-testid={`pay-sheet-method-${option.id}`}
                            data-active={active ? "true" : "false"}
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors",
                              active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
                              isSubmitting && "cursor-not-allowed opacity-70"
                            )}
                          >
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-card">
                              <Icon className="size-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium">{option.title}</span>
                              <span className="block text-xs text-muted-foreground">{option.subtitle}</span>
                            </span>
                            <RadioGroupItem value={option.id} aria-label={option.title} />
                          </label>
                        )
                      })}
                    </RadioGroup>
                  )}
                </section>

                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  data-testid="submit-payment-button"
                  disabled={submitDisabled}
                  onClick={confirm}
                >
                  <Lock />
                  {isSubmitting ? (
                    t("ui.react.features.user.pay_sheet.processing")
                  ) : (
                    <>
                      {submitLabel}{" "}
                      {/* The total moves with every tip and item tap, so it is
                          tabular here too — it was the one figure on the sheet
                          rendered as plain interpolated text. */}
                      <span className="tabular-nums">{formatMoney(total, { currency })}</span>
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn("mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground", className)}>
      {children}
    </p>
  )
}

function ModeCard({
  testId,
  icon,
  title,
  hint,
  onSelect,
}: {
  testId: string
  icon: React.ReactNode
  title: string
  hint: string
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className="flex items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-muted/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-card">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  )
}

/** True when `ids` is exactly the set of ids in `orders` — order-insensitive. */
function sameSelection(ids: number[], orders: BillOrderLine[]) {
  if (orders.length === 0 || ids.length !== orders.length) return false
  const selected = new Set(ids)
  return orders.every((order) => selected.has(order.id))
}

function ShortcutToggle({
  testId,
  icon,
  label,
  pressed,
  onClick,
  disabled = false,
}: {
  testId: string
  icon: React.ReactNode
  label: string
  pressed: boolean
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <Button
      type="button"
      variant={pressed ? "default" : "outline"}
      size="icon-sm"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      data-active={pressed ? "true" : "false"}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </Button>
  )
}

// Focus a freshly mounted field and select what is in it, so a seeded amount is
// replaced by the first keystroke. A callback ref rather than `autoFocus` plus
// `onFocus`, because it has to happen once — on the mount — and not again every
// time the diner comes back to correct a digit.
function selectOnMount(node: HTMLInputElement | null) {
  if (!node) return
  node.focus()
  node.select()
}

// Paid and reserved lines stay visible but cannot be selected: the diner needs
// to see what the table already covered, not a list that silently shrinks.
function OrderRow({
  order,
  currency,
  owner,
  checked,
  onToggle,
}: {
  order: BillOrderLine
  currency: string
  /** The roster entry that owns this line, or null for a staff-placed one. */
  owner: BillParticipant | null
  checked: boolean
  onToggle: () => void
}) {
  const paid = order.paymentStatus === "paid"
  const reserved = order.paymentStatus === "locked"
  const disabled = paid || reserved

  return (
    <li>
      <label
        data-testid={`pay-item-${order.id}`}
        data-active={checked ? "true" : "false"}
        className={cn(
          // Two lines of content clear the 44px floor on their own, so there is
          // no desktop step-down to declare — it would be inert.
          "flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
          checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        {/* `text-primary-foreground`, not `text-white`: --primary inverts between
            themes, so a hardcoded white check disappeared on dark. */}
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded border",
            checked ? "border-primary bg-primary text-primary-foreground" : "border-input"
          )}
          aria-hidden="true"
        >
          {checked && <Check className="size-3" />}
        </span>
        {/* No `aria-label`: it used to be `order.name`, which overrode the
            label's own text and left a screen reader announcing the dish and
            nothing else — not who ordered it, not the price, not whether it was
            already paid. The label content is exactly what should be read. */}
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">
            {order.quantity > 1 && (
              <span className="tabular-nums text-muted-foreground">{order.quantity}× </span>
            )}
            {order.name}
          </span>
          {/* Paying for someone else's food is a deliberate act. Naming whose
              food it is turns the checkbox from a guess into a decision. */}
          <OrderOwner order={order} owner={owner} />
        </span>
        {paid && (
          <span className="shrink-0 text-xs font-medium text-success">
            {t("ui.react.features.user.pay_sheet.already_paid")}
          </span>
        )}
        {reserved && (
          <span className="shrink-0 text-xs font-medium text-warning">
            {t("ui.react.features.user.pay_sheet.reserved_by_other")}
          </span>
        )}
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {formatMoney(parseDecimal(order.lineTotal), { currency })}
        </span>
      </label>
    </li>
  )
}

function OrderOwner({ order, owner }: { order: BillOrderLine; owner: BillParticipant | null }) {
  const isMine = order.isMine

  return (
    <span className="mt-0.5 flex items-center gap-1.5">
      {/* Both marks are aria-hidden: they abbreviate the name that follows, so
          announcing them turns "Beto Salas" into "BS, Beto Salas". */}
      {owner ? (
        // `size="sm"` rather than a hand-set size: the primitive steps its own
        // fallback type down with it, so the initials stay on the ramp.
        <Avatar size="sm" aria-hidden="true">
          <AvatarFallback className={cn("font-bold", isMine && "bg-brand-subtle text-brand-ink")}>
            {owner.initials}
          </AvatarFallback>
        </Avatar>
      ) : (
        // Counter orders belong to the house, not to a table-mate, so they get
        // the role mark rather than a set of initials the diner cannot place.
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <ConciergeBell className="size-3" />
        </span>
      )}
      <span className="truncate text-xs text-muted-foreground">
        {isMine ? t("ui.user.common.me") : order.whoName}
      </span>
    </span>
  )
}
